import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pencil, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";


export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editService, setEditService] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchServices = async () => {
    try {
      const res = await api.get(`/services?show_all=true`);
      setServices(res.data);
    } catch (err) {
      toast.error("Erreur chargement des services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, []);

  const handleEdit = (service) => {
    setEditService(service);
    setEditData({ ...service });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/services/${editService.id}`, editData);
      toast.success("Service mis a jour");
      setEditService(null);
      fetchServices();
    } catch (err) {
      toast.error("Erreur lors de la mise a jour");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setEditData((p) => ({ ...p, [field]: value }));
  const updateCopy = (field, value) =>
    setEditData((p) => ({ ...p, copywriting: { ...p.copywriting, [field]: value } }));

  return (
    <div className="space-y-6" data-testid="services-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Services</h1>
        <p className="text-sm text-slate-500 mt-1">{services.length} service(s)</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nom</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Prix</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Frequence</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Actif</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((s) => (
                  <TableRow key={s.id || s.slug} className="hover:bg-slate-50/50" data-testid={`service-row-${s.slug}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{s.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">{s.type_service}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{s.type_client}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.prix_moyen}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.fréquence}</TableCell>
                    <TableCell>
                      <div className={`w-2.5 h-2.5 rounded-full ${s.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} data-testid={`edit-service-${s.slug}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editService} onOpenChange={() => setEditService(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="edit-service-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le service</DialogTitle>
            <DialogDescription>{editData.name}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="copywriting">Copywriting</TabsTrigger>
                <TabsTrigger value="blocs">B2B / B2C</TabsTrigger>
                <TabsTrigger value="seo">SEO</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Nom</label>
                  <Input value={editData.name || ""} onChange={(e) => updateField("name", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Slug</label>
                  <Input value={editData.slug || ""} onChange={(e) => updateField("slug", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
                  <Textarea value={editData.description || ""} onChange={(e) => updateField("description", e.target.value)} rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Type service</label>
                    <Select value={editData.type_service} onValueChange={(v) => updateField("type_service", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="opérationnel">Operationnel</SelectItem>
                        <SelectItem value="segment_b2b">Segment B2B</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Type client</label>
                    <Select value={editData.type_client} onValueChange={(v) => updateField("type_client", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B2C">B2C</SelectItem>
                        <SelectItem value="B2B">B2B</SelectItem>
                        <SelectItem value="mixte">Mixte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Prix moyen</label>
                    <Input value={editData.prix_moyen || ""} onChange={(e) => updateField("prix_moyen", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Frequence</label>
                    <Select value={editData.fréquence} onValueChange={(v) => updateField("fréquence", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ponctuel">Ponctuel</SelectItem>
                        <SelectItem value="recurrent">Recurrent</SelectItem>
                        <SelectItem value="les_deux">Les deux</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="copywriting" className="space-y-3">
                {["hook", "problème", "agitation", "solution", "preuve", "cta"].map((field) => (
                  <div key={field}>
                    <label className="text-sm font-medium text-slate-700 mb-1 block capitalize">{field}</label>
                    <Textarea
                      value={editData.copywriting?.[field] || ""}
                      onChange={(e) => updateCopy(field, e.target.value)}
                      rows={field === "cta" || field === "hook" ? 2 : 3}
                    />
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="blocs" className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Bloc B2B</label>
                  <Textarea value={editData.bloc_b2b || ""} onChange={(e) => updateField("bloc_b2b", e.target.value)} rows={4} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Bloc B2C</label>
                  <Textarea value={editData.bloc_b2c || ""} onChange={(e) => updateField("bloc_b2c", e.target.value)} rows={4} />
                </div>
              </TabsContent>

              <TabsContent value="seo" className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Meta title</label>
                  <Input value={editData.meta_title || ""} onChange={(e) => updateField("meta_title", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Meta description</label>
                  <Textarea value={editData.meta_description || ""} onChange={(e) => updateField("meta_description", e.target.value)} rows={3} />
                </div>
              </TabsContent>
            </Tabs>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditService(null)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="save-service-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
